import { all, call, delay, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actionTypes from 'appRedux/actions/chargeSchedule/ChargeReservationActionType';
import * as actionCreators from 'appRedux/actions/chargeSchedule/ChargeReservation';
import { userSignOut } from 'appRedux/actions/Auth';
import { API_URL_V1, API_PMS_HEADERS } from 'constants/ApiSetting';
import { APIGetRequest, APIPostRequest } from 'util/connection';
import { getItems } from 'util/localStorage';
import { ApiResponse } from 'constants/ActionTypes';
import {
  chargeScheduleType,
  RateBreakdownGroup,
  RateScheduleReservationRequest,
} from 'appRedux/reducers/chargeSchedule/chargeType';
import { buildRateUpdatePayloads } from './rateUpdatePayload';
import { executeRateUpdateRequests } from './rateUpdateRequest';

const getAuthorizationHeaders = function* () {
  const { access_token } = yield call(getItems, ['access_token']);
  API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;
  return API_PMS_HEADERS;
};

const normalizeRequest = (payload: any): RateScheduleReservationRequest =>
  typeof payload === 'object' && payload !== null
    ? payload
    : { bookingID: Number(payload) };

const getRpcError = (response: ApiResponse, fallback: string) => {
  const data = response?.data || {};
  return {
    message: data.result_msg || data.message || fallback,
    result_code: data.result_code || 'ERROR',
    msg_code: data.msg_code,
  };
};

function* chargeReservationRequestProcess({ payload }: actionTypes.ChargeReservationRequest) {
  const request = normalizeRequest(payload);
  const endpoint = request.isGroup
    ? 'pms/rpc/reservation/group_booking/rate_schedule/get'
    : 'pms/rpc/reservation/booking/rate_schedule/get';
  const body = {
    booking_id: Number(request.bookingID),
    ...(request.isGroup ? { scope: request.scope || 'all' } : {}),
    ...(request.itemId ? { item_id: request.itemId } : {}),
  };

  try {
    const headers = yield* getAuthorizationHeaders();
    const response: ApiResponse = yield APIPostRequest(
      `${API_URL_V1}${endpoint}`,
      body,
      headers
    );
    const { result_code, result_data } = response.data || {};

    if (response.status === 200 && result_code === '0') {
      const result: chargeScheduleType[] = Array.isArray(result_data) ? result_data : [];
      yield put(actionCreators.chargeReservationDataSourceSuccess(result));
    } else {
      const error = getRpcError(response, 'Unable to load rate schedule');
      yield put(actionCreators.chargeReservationDataSourceSuccess([]));
      yield put(
        actionCreators.chargeReservationProcessSuccess({
          status: false,
          action: 'rateSchedule',
          ...error,
        })
      );
      if (response.status === 403 && response.data?.data) yield put(userSignOut());
    }
  } catch (error) {
    yield put(actionCreators.chargeReservationDataSourceSuccess([]));
    yield put(
      actionCreators.chargeReservationProcessSuccess({
        status: false,
        action: 'rateSchedule',
        message: error,
        result_code: 'ERROR',
      })
    );
  }
  yield delay(100);
}

function* chargeReservationBreakdownProcess({
  payload,
}: actionTypes.ChargeReservationBreakdownRequest) {
  const request = normalizeRequest(payload);
  const endpoint = request.isGroup
    ? 'pms/rpc/reservation/group_booking/rate_breakdown/get'
    : 'pms/rpc/reservation/booking/rate_breakdown/get';
  const body = {
    booking_id: Number(request.bookingID),
    ...(request.isGroup ? { scope: request.scope || 'all' } : {}),
    ...(request.itemId ? { item_id: request.itemId } : {}),
  };

  try {
    const headers = yield* getAuthorizationHeaders();
    const response: ApiResponse = yield APIPostRequest(
      `${API_URL_V1}${endpoint}`,
      body,
      headers
    );
    const { result_code, result_data } = response.data || {};

    if (response.status === 200 && result_code === '0') {
      const result: RateBreakdownGroup[] = Array.isArray(result_data) ? result_data : [];
      yield put(actionCreators.chargeReservationBreakdownSuccess(result));
    } else {
      const error = getRpcError(response, 'Unable to load rate breakdown');
      yield put(actionCreators.chargeReservationBreakdownSuccess([]));
      yield put(
        actionCreators.chargeReservationProcessSuccess({
          status: false,
          action: 'rateBreakdown',
          ...error,
        })
      );
      if (response.status === 403 && response.data?.data) yield put(userSignOut());
    }
  } catch (error) {
    yield put(actionCreators.chargeReservationBreakdownSuccess([]));
    yield put(
      actionCreators.chargeReservationProcessSuccess({
        status: false,
        action: 'rateBreakdown',
        message: error,
        result_code: 'ERROR',
      })
    );
  }
  yield delay(100);
}

function* chargeReservationUpdateProcess({ payload }: actionTypes.ChargeReservationUpdate) {
  const { bookingID, inlineUpdates = [], trans = [], valueUpdate = {}, isGroup = false } =
    payload || {};
  const updateRequests = buildRateUpdatePayloads({
    context: { booking_id: Number(bookingID) },
    inlineUpdates,
    transactions: trans,
    valueUpdate,
  });
  const endpoint = isGroup
    ? 'pms/rpc/reservation/group_booking/rate_schedule/update'
    : 'pms/rpc/reservation/booking/rate_schedule/update';

  try {
    const headers = yield* getAuthorizationHeaders();
    const { success, result } = yield* executeRateUpdateRequests(
      `${API_URL_V1}${endpoint}`,
      updateRequests,
      headers
    );

    yield put(
      actionCreators.chargeReservationProcessSuccess({
        status: success,
        action: 'updateRateSchedule',
        message:
          result.result_msg || (success ? 'Save success' : 'Unable to save rate schedule'),
        result_code: result.result_code || 'ERROR',
        msg_code: result.msg_code,
      })
    );
  } catch (error) {
    yield put(
      actionCreators.chargeReservationProcessSuccess({
        status: false,
        action: 'updateRateSchedule',
        message: error,
        result_code: 'ERROR',
      })
    );
  }
  yield delay(100);
}

function* chargeReservationDetailProcess({ payload }: actionTypes.ChargeReservationDetailRequest) {
  const headers = yield* getAuthorizationHeaders();
  const url = `${API_URL_V1}pms/reservation/booking/${payload}`;

  try {
    const response: ApiResponse = yield APIGetRequest(url, headers);
    if (response.status === 200) {
      const { booking_name, status, details, book_type } = response.data;
      yield put(
        actionCreators.chargeReservationDetailDataSourceSuccess({
          booking_name,
          status,
          details: Array.isArray(details) ? details : [],
          book_type,
        })
      );
    } else if (response.status === 403 && response.data?.data) {
      yield put(userSignOut());
    }
  } catch (error) {
    yield put(
      actionCreators.chargeReservationProcessSuccess({
        status: false,
        action: 'reservationDetail',
        message: error,
        result_code: 'ERROR',
      })
    );
  }
  yield delay(100);
}

function* chargeReservationRequest() {
  yield takeLatest(actionTypes.CHARGE_RESERVATION_REQUEST, chargeReservationRequestProcess);
}

function* chargeReservationBreakdown() {
  yield takeLatest(
    actionTypes.CHARGE_RESERVATION_BREAKDOWN_REQUEST,
    chargeReservationBreakdownProcess
  );
}

function* chargeReservationUpdate() {
  yield takeEvery(actionTypes.CHARGE_RESERVATION_UPDATE, chargeReservationUpdateProcess);
}

function* chargeReservationDetail() {
  yield takeEvery(actionTypes.CHARGE_RESERVATION_DETAIL_REQUEST, chargeReservationDetailProcess);
}

export default function* rootSaga() {
  yield all([
    fork(chargeReservationRequest),
    fork(chargeReservationBreakdown),
    fork(chargeReservationUpdate),
    fork(chargeReservationDetail),
  ]);
}
